import { Box, debounce, InputLabel, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import useStyles from './styles';
import { Field, FieldProps } from 'formik';
import { api } from 'modules/global/adapters';

type Props = {
  showText: boolean | undefined;
  label: string | undefined;
  translationKey: string | undefined;
  textLabel: string | undefined;
  roles: string[] | undefined;
};

const IdentificationSelectAutoComplete = ({
  showText,
  label,
  translationKey,
  textLabel,
  roles,
}: Props) => {
  const { t } = useTranslation(translationKey);
  const { classes } = useStyles();
  const [options, setOptions] = React.useState<{ name: string; nxtEntityId: string }[]>([]);
  const [inputValue, setInputValue] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);

  // Debounced fetch function to get data from API
  const fetch = React.useMemo(
    () =>
      debounce(async (input: string) => {
        try {
          // Fetch the data from the API
          setIsLoading(true);
          const response = await api.entitiesByLikeParamaters.getEntitiesByLikeParams({
            entityName: input,
            roles: roles,
          });

          // Assuming response.items contains objects with `name` and `nxtEntityId`
          const optionsArray = response
            ? response.items.map((x) => ({ name: x.name, nxtEntityId: x.nxtEntityId }))
            : [];
          setOptions(optionsArray);
          setIsLoading(false);
        } catch (error) {
          console.error('Error fetching options:', error);
          setIsLoading(false);
        }
      }, 400),
    []
  );

  // Trigger the fetch function whenever inputValue changes
  React.useEffect(() => {
    if (inputValue.length >= 3) {
      fetch(inputValue);
    } else {
      setOptions([]);
    }
  }, [inputValue, fetch]);

  return (
    <React.Fragment>
      <InputLabel className={classes.label}>{label}</InputLabel>
      <Box flexDirection="row" display="flex">
        <Box flexGrow={1}>
          <Field name="identificationCode" id="identificationCode">
            {({ field, form }: FieldProps) => {
              // Map field.value (nxtEntityId) back to the corresponding option object
              const selectedOption =
                options.find((option) => option.nxtEntityId === field.value) || null;
              return (
                <>
                  {showText && (
                    <InputLabel className={classes.label}>
                      {t('selectParty.identificationTo')}
                    </InputLabel>
                  )}
                  <Autocomplete
                    value={selectedOption} // Only allow valid options to be selected
                    options={options}
                    noOptionsText="Start typing at least 3 characters to get suggestions"
                    getOptionLabel={(option) => option.name || ''}
                    onChange={(event, newValue) => {
                      form.setFieldValue('identificationCode', newValue?.nxtEntityId || null);
                    }}
                    loading={isLoading}
                    loadingText="Retrieving suggestions..."
                    onInputChange={(event, newInputValue) => {
                      setInputValue(newInputValue); // Update inputValue
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        value={inputValue}
                        label={textLabel || ''}
                      />
                    )}
                  />
                </>
              );
            }}
          </Field>
        </Box>
      </Box>
    </React.Fragment>
  );
};

export default IdentificationSelectAutoComplete;
